// src/utils/ThemeManager.ts
export type Theme = 'light' | 'dark' | 'system' | 'blue-gray' | 'sage' | 'lavender';

export class ThemeManager {
  private static instance: ThemeManager;
  private currentTheme: Theme = 'light'; // Default to 'light'
  private listeners: Set<(theme: string) => void> = new Set();

  private constructor() {
    // Load saved theme preference
    const savedTheme = localStorage.getItem('emcog-theme') as Theme;
    if (savedTheme && this.isValidTheme(savedTheme)) {
      this.currentTheme = savedTheme;
    } else {
      // If no saved theme, default to light
      this.currentTheme = 'light';
    }
    
    // Initialize theme on load
    this.applyTheme();
    
    // Listen for system theme changes
    if (window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this.currentTheme === 'system') {
          this.applyTheme();
        }
      });
    }
  }

  private isValidTheme(theme: string): theme is Theme {
    return ['light', 'dark', 'system', 'blue-gray', 'sage', 'lavender'].includes(theme);
  }

  public static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }

  public setTheme(theme: Theme): void {
    if (!this.isValidTheme(theme)) {
      console.warn(`Invalid theme: ${theme}, defaulting to light`);
      theme = 'light';
    }
    
    this.currentTheme = theme;
    localStorage.setItem('emcog-theme', theme);
    this.applyTheme();
    this.notifyListeners(theme);
  }

  public getTheme(): Theme {
    return this.currentTheme;
  }

  public getEffectiveTheme(): 'light' | 'dark' | 'blue-gray' | 'sage' | 'lavender' {
    if (this.currentTheme === 'system') {
      return this.getSystemTheme();
    }
    return this.currentTheme as any;
  }

  private getSystemTheme(): 'light' | 'dark' {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }

  private applyTheme(): void {
    const root = document.documentElement;
    const body = document.body;
    
    // Remove existing theme classes
    root.removeAttribute('data-theme');
    body.classList.remove('light', 'dark', 'blue-gray', 'sage', 'lavender');
    
    // Determine effective theme
    let effectiveTheme = this.currentTheme;
    if (this.currentTheme === 'system') {
      effectiveTheme = this.getSystemTheme();
    }
    
    // Apply new theme
    root.setAttribute('data-theme', effectiveTheme);
    body.classList.add(effectiveTheme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const themeColors: Record<string, string> = {
      'light': '#faf9f7',      // Soft cream
      'dark': '#0a0a0f',       // Neural dark
      'blue-gray': '#f8fafb',  // Cool mist
      'sage': '#f7faf7',       // Sage garden
      'lavender': '#faf8fb'    // Lavender dream
    };
    
    const color = themeColors[effectiveTheme] || '#ffffff';
    
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', color);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = color;
      document.head.appendChild(meta);
    }
  }

  public addListener(listener: (theme: string) => void): void {
    this.listeners.add(listener);
  }

  public removeListener(listener: (theme: string) => void): void {
    this.listeners.delete(listener);
  }

  private notifyListeners(theme: string): void {
    this.listeners.forEach(listener => listener(theme));
  }
}